import React, { useEffect, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { Box, Link, Typography } from '@mui/material'
import SimpleReactLightbox, { SRLWrapper } from 'simple-react-lightbox'
import Skeleton from 'react-loading-skeleton'
import AnimationWrapper from '../helpers/Animations'
import MediaRender from '../components/general/MediaRender'
import Spacer from '../components/general/Spacer'
import { prepCaption } from '../helpers/Functions'
import styled from 'styled-components'
import themePalette from '../theme/palette'

const Item = styled(Box)(({ theme }) => ({
    fontFamily: 'museo-sans',
    position: 'relative',
    overflow: 'hidden',

    '.caption': {
        backgroundColor: 'rgba(70,70,70,0.5)',
        color: 'white',
        fontSize: '12px',
        fontWeight: 500,
        lineHeight: '16px',
        position: 'absolute',
        padding: '12px',
        bottom: '-126px',
        left: 0,
        right: 0,
        transition: 'bottom .45s cubic-bezier(.785,.135,.15,.86) 0s',
        textTransform: 'none',
        zIndex: 2,
    },

    '&:hover': {
        '.caption': {
            bottom: '0px',
        },
    },
}))

function GalleryGrid({ options }) {
    const theme = useTheme()
    const bigScreen = useMediaQuery(theme.breakpoints.up('desktop'))
    const midScreen = useMediaQuery(theme.breakpoints.up('md'))
    const smallScreen = useMediaQuery(theme.breakpoints.up('sm'))
    const [visible, setVisible] = useState(true)
    const [data, setData] = useState([])
    const srlOptions = {
        buttons: {
            showDownloadButton: false,
        },
    }
    
    useEffect(() => {
        if (bigScreen) {
            setVisible(options?.responsive?.hideOnDesktop ? false : true)
        } else if (midScreen) {
            setVisible(options?.responsive?.hideOnTablet ? false : true)
        } else {
            setVisible(options?.responsive?.hideOnMobile ? false : true)
        }
    }, [bigScreen, midScreen, smallScreen])
    
    useEffect(() => {
        if (
            options.media &&
            options.media.set &&
            options.media &&
            options.media.set.length
        ) {
            const items = []
            Object.entries(options.media.set).map(([key, image]) => {
                if (!image) {
                    return;
                }

                const contents = (
                    <>
                        <AnimationWrapper type='fade-up'>
                            {image?.caption ? (
                                <Box
                                    className='caption'
                                    py={2}
                                >
                                    <Typography
                                        color={themePalette.white.main}
                                        fontSize='13px'
                                        mx={2}
                                    >
                                        {prepCaption(image?.caption)}
                                    </Typography>
                                </Box>
                            ) : null}
                            <Box my={{ xxxs: 3, md: 0 }}>
                                <MediaRender
                                    file={image}
                                    options={{
                                        backgroundVideo: false,
                                        backgroundImage: false,
                                        hoverzoom: true,
                                        teamPortrait: options.teamPortrait,
                                        portrait: options?.portrait ?? false,
                                    }}
                                    alt={image?.caption ?? ''}
                                />
                            </Box>
                        </AnimationWrapper>
                    </>
                )

                items.push(
                    // external videos (video/youtube, video/vimeo) should not be links - this is to make the popup work correctly
                    image.media_type && image.media_type.startsWith('video/') ?
                        <Box key={`${key}-${image.filename}`} sx={{cursor: 'pointer'}} >
                            {contents}
                        </Box>
                        :
                        <Item>
                            <Link
                                key={`${key}-${image.filename}`}
                                href={`${process.env.GATSBY_CLOUDINARY_IMAGE_URL}${image.filepath}${image.filename}`}
                            >
                                {contents}
                            </Link>
                        </Item>
                )
            })
            setData(items)
        }
    }, [options])

    return (
        <Spacer spacing={options.spacing} display={visible ? false : 'none'}>
            {data.length ? (
                <AnimationWrapper type='fade-up'>
                    <Box>
                        <Typography
                            variant='h2'
                            color='red.main'
                            mb={8}
                        >
                            {options.title}
                        </Typography>
                        <SimpleReactLightbox>
                            <SRLWrapper options={srlOptions}>
                                <Box
                                    variant='container'
                                    py='md'
                                    sx={{
                                        display: { xxxs: 'block', md: 'grid' },
                                        gridGap: 18,
                                        gridTemplateColumns: [
                                            '1fr',
                                            '1fr',
                                            '1fr',
                                            'repeat(2, 1fr)',
                                            'repeat(2, 1fr)',
                                            `repeat(${options.numOfColumns}, 1fr)`,
                                        ],
                                    }}
                                >
                                    {data}
                                </Box>
                            </SRLWrapper>
                        </SimpleReactLightbox>
                    </Box>
                </AnimationWrapper>
            ) : null}
        </Spacer>
    )
}

export default GalleryGrid
